"use client";

import React, { useRef } from "react";
import { CircleQuestionMark as HelpCircle, Phone, ArrowRight, ShieldCheck } from 'lucide-react';
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
const faqs = [{
  id: "item-1",
  question: "Do BMF Scaffolding cover Beaconsfield?",
  answer: "Yes. We regularly supply and erect scaffolding throughout Beaconsfield and the surrounding South Buckinghamshire area for residential properties, commercial sites, and trade contractors."
}, {
  id: "item-2",
  question: "What types of scaffolding can you provide in Beaconsfield?",
  answer: "We provide independent access scaffolding, temporary roofs, edge protection, chimney stacks, birdcage scaffolds, solar panel access systems, and tailored access solutions for new builds and refurbishments."
}, {
  id: "item-3",
  question: "Can you provide scaffolding for roof work?",
  answer: "Yes. We build perimeter edge protection, handrails, chimney scaffolds, solar installer platforms, and full temporary roof structures to keep roofing contractors working safely at height."
}, {
  id: "item-4",
  question: "Do you work with contractors and developers?",
  answer: "Yes. We partner with main contractors, residential developers, local councils, facilities managers, and specialist trades, operating under full NASC and CHAS accreditations."
}, {
  id: "item-5",
  question: "Can scaffolding be installed where access is restricted?",
  answer: "Yes. Our scaffolders carry out site surveys to design bespoke access solutions for narrow passageways, uneven ground, tight boundaries, public footpaths, and delicate structures."
}, {
  id: "item-6",
  question: "How do I request a quote in Beaconsfield?",
  answer: "Submit your project requirements and site details through our online quote form or speak directly with our team. We will review the specifications and arrange a site visit if required."
}];
export default function BeaconsfieldFaq() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const el = containerRef.current;
    if (!el) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".faq-header", {
        y: 24
      });
      gsap.to(".faq-header", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".faq-item-anim", {
        y: 20
      });
      gsap.to(".faq-item-anim", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.08,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".faq-accordion-wrap",
          start: "top 85%",
          once: true
        }
      });
      gsap.set(".faq-sidebar", {
        y: 24
      });
      gsap.to(".faq-sidebar", {
        opacity: 1,
        y: 0,
        duration: 0.6,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".faq-sidebar",
          start: "top 88%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set([".faq-header", ".faq-item-anim", ".faq-sidebar"], {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="beaconsfieldfaq" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground border-b border-border">
      <div ref={containerRef} className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="faq-header max-w-2xl mb-12" style={{
        opacity: 0
      }}>
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="outline" className="text-xs uppercase tracking-wider font-semibold">
              <HelpCircle className="size-3 text-primary mr-1" />
              Beaconsfield access
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Frequently asked questions
          </h2>
          <p className="mt-3 text-base md:text-lg text-muted-foreground">
            Clear information about our scaffolding hire, site surveys, and safety standards across Beaconsfield.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          <div className="lg:col-span-8">
            <div className="faq-accordion-wrap bg-card rounded-xl border border-border p-4 md:p-6 shadow-sm">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full">
                {faqs.map((faq, i) => <div key={faq.id} data-index={i} className="faq-item-anim" style={{
                opacity: 0
              }}>
                    <AccordionItem value={faq.id} className="border-border">
                      <AccordionTrigger className="text-base font-semibold text-foreground hover:text-primary hover:no-underline py-4 text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-sm md:text-base text-muted-foreground leading-relaxed pb-4">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </div>)}
              </Accordion>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="faq-sidebar bg-muted/50 rounded-xl border border-border p-6 shadow-sm flex flex-col gap-6" style={{
            opacity: 0
          }}>
              <div>
                <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary mb-2">
                  <ShieldCheck className="size-4" />
                  <span>NASC &amp; CHAS</span>
                </div>
                <h3 className="text-lg font-bold text-foreground">
                  Need scaffolding in Beaconsfield?
                </h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  Contact our operational team to arrange a site survey or discuss project requirements with Pete Wood and our qualified scaffolders.
                </p>
              </div>

              <div className="flex flex-col gap-3">
                <Button asChild className="w-full justify-center">
                  <Link to="/ContactRequestQuotePage">
                    Request a quote
                    <ArrowRight className="size-4 ml-1.5" />
                  </Link>
                </Button>

                <Button variant="outline" asChild className="w-full justify-center">
                  <Link to="/AreasWeCoverPage">
                    View all areas covered
                  </Link>
                </Button>
              </div>

              <div className="border-t border-border pt-4 text-xs text-muted-foreground flex items-center justify-between">
                <span>Direct trade inquiries</span>
                <a href="tel:01494000000" className="font-medium text-foreground hover:text-primary flex items-center gap-1">
                  <Phone className="size-3 text-primary" />
                  <span>Call our team</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}